/**
 * Loads a Lordicon `.lottie` file into icon data the `lord-icon` element accepts.
 *
 * Lordicon's downloads are not Lottie JSON and are not dotLottie archives. They
 * are the Lottie JSON base64-encoded and then XOR-masked, which is why pointing
 * the element's `src` straight at one produced an upgraded, correctly-sized, and
 * completely empty element: `loadIconData` fetched the file, failed to parse it,
 * and had nothing to hand the player. Nothing threw, so it looked like styling.
 *
 * The element also accepts icon data directly through its `icon` property, so the
 * licensed files stay exactly as delivered and the decode happens here.
 */

/** The mask Lordicon applies to the base64 payload. */
const MASK = 0x2a;

/** Decoded icons, keyed by URL: the same mark is used on several surfaces. */
const cache = new Map<string, Promise<unknown>>();

/** Turns one downloaded `.lottie` body into a Lottie animation object. */
export function decodeLordiconAsset(body: string): unknown {
  const trimmed = body.trim();

  // A plain JSON export needs no decoding, so callers can mix the two freely.
  if (trimmed.startsWith("{")) return JSON.parse(trimmed);

  const binary = atob(trimmed);
  let json = "";
  for (let index = 0; index < binary.length; index += 1) {
    json += String.fromCharCode(binary.charCodeAt(index) ^ MASK);
  }
  return JSON.parse(json);
}

/**
 * Fetches and decodes an icon, once per URL.
 *
 * Rejections are cached too, deliberately: a missing icon should not have every
 * mount of every surface retrying the same 404.
 */
export function loadLordiconAsset(url: string): Promise<unknown> {
  const existing = cache.get(url);
  if (existing) return existing;

  const pending = fetch(url)
    .then(response => {
      if (!response.ok) throw new Error(`icon ${url} responded ${response.status}`);
      return response.text();
    })
    .then(decodeLordiconAsset);

  cache.set(url, pending);
  return pending;
}
